import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, LogOut, Sparkles, Loader2, FileText, ArrowLeft, Crown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import GuardWork from "@/components/GuardWork";
import BanAppeal from "@/components/BanAppeal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const effectiveUser = user || location.state?.user;

  useEffect(() => {
    if (user === false) navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    if (effectiveUser) loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveUser?.user_id]);

  const loadFiles = async () => {
    try {
      const res = await fetch(`${API}/files/mine`, { credentials: "include" });
      if (res.ok) setFiles((await res.json()).files);
    } catch { /* silent */ }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API}/files/upload?kind=document`, { method: "POST", body: form, credentials: "include" });
      if (!res.ok) throw new Error((await res.json()).detail || "Upload failed");
      toast.success("Document uploaded securely.");
      loadFiles();
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const generateBadge = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/media/badge`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Generation failed");
      toast.success("Founding badge generated.");
      loadFiles();
    } catch (err) {
      toast.error(err.message || "Badge generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  if (!effectiveUser) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#07090e]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (effectiveUser.status === "banned") {
    return <BanAppeal user={effectiveUser} />;
  }

  const badges = files.filter((f) => f.kind === "badge");
  const docs = files.filter((f) => f.kind !== "badge");

  return (
    <div data-testid="account-page" className="min-h-screen bg-[#07090e] pb-24">
      <header className="border-b border-white/5 bg-[#07090e]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <button data-testid="account-back-btn" onClick={() => navigate("/")} className="flex items-center gap-2 text-slate-400 transition-colors hover:text-amber-500">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-mono-tag text-[10px]">Back to site</span>
          </button>
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-md border border-amber-500/30">
              <img src="/icons/icon-192.png" alt="GuardGig28 logo" className="h-full w-full object-cover" />
            </span>
            <span className="font-display text-lg font-extrabold uppercase text-slate-50">Guard<span className="text-amber-500">Gig</span>28</span>
          </div>
          <button
            data-testid="account-logout-btn"
            onClick={async () => { await logout(); navigate("/"); }}
            className="flex items-center gap-2 text-slate-400 transition-colors hover:text-red-400"
          >
            <LogOut className="h-4 w-4" />
            <span className="font-mono-tag text-[10px]">Sign out</span>
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 pt-12 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="font-mono-tag text-[11px] text-amber-500">Member HQ</p>
          <h1 className="font-display mt-3 text-4xl font-black uppercase tracking-tight text-slate-50 sm:text-5xl">
            {effectiveUser.name}
          </h1>
          {effectiveUser.role === "admin" && (
            <button
              data-testid="account-admin-link"
              onClick={() => navigate("/admin")}
              className="mt-4 inline-flex items-center gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-2 font-mono-tag text-[10px] text-red-400 transition-all hover:bg-red-500/20"
            >
              Open admin dashboard
            </button>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span data-testid="account-email" className="text-sm text-slate-400">{effectiveUser.email}</span>
            <span className={`rounded-full px-3 py-1 font-mono-tag text-[9px] ${effectiveUser.role === "admin" ? "border border-red-500/40 bg-red-500/10 text-red-400" : "border border-emerald-500/40 bg-emerald-500/10 text-emerald-400"}`}>
              {effectiveUser.role === "admin" ? "Admin" : "Member"} · {effectiveUser.auth_provider === "google" ? "Google" : "Email"}
            </span>
          </div>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-xl border border-white/8 bg-[#0f141c] p-8"
          >
            <div className="flex items-center gap-3">
              <Upload className="h-5 w-5 text-amber-500" />
              <h2 className="font-display text-xl font-bold uppercase text-slate-50">Guard documents</h2>
            </div>
            <p className="mt-2 text-sm text-slate-500">Upload your guard card or license (PDF/JPG/PNG, max 10 MB). Stored encrypted, visible only to you.</p>
            <label
              data-testid="doc-upload-label"
              className="mt-6 flex h-28 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-white/15 text-slate-500 transition-all hover:border-amber-500/50 hover:text-amber-500"
            >
              {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <Upload className="h-6 w-6" />}
              <span className="font-mono-tag text-[9px]">{uploading ? "Uploading…" : "Click to upload"}</span>
              <input data-testid="doc-upload-input" type="file" accept="image/*,.pdf" className="hidden" onChange={upload} disabled={uploading} />
            </label>
            <ul className="mt-5 space-y-2">
              {docs.map((f) => (
                <li key={f.id} data-testid={`doc-item-${f.id}`} className="flex items-center justify-between rounded-md border border-white/5 bg-[#07090e] px-4 py-2.5">
                  <span className="flex items-center gap-2 text-xs text-slate-300">
                    <FileText className="h-3.5 w-3.5 text-slate-500" />
                    {f.original_filename}
                  </span>
                  <a data-testid={`doc-download-${f.id}`} href={`${API}/files/${f.storage_path}`} target="_blank" rel="noreferrer" className="font-mono-tag text-[9px] text-amber-500 hover:underline">View</a>
                </li>
              ))}
              {docs.length === 0 && <p className="font-mono-tag py-2 text-[9px] text-slate-600">No documents yet</p>}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="rounded-xl border border-amber-500/25 bg-[#0f141c] p-8"
          >
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-amber-500" />
              <h2 className="font-display text-xl font-bold uppercase text-slate-50">AI founding badge</h2>
            </div>
            <p className="mt-2 text-sm text-slate-500">Generate your personalized founding-member emblem with Gemini image generation.</p>
            <button
              data-testid="badge-generate-btn"
              onClick={generateBadge}
              disabled={generating}
              className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-md bg-amber-500 font-display text-sm font-bold uppercase text-[#07090e] transition-all hover:bg-amber-400 disabled:opacity-50"
            >
              {generating ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : "Generate my badge"}
            </button>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {badges.map((f) => (
                <img key={f.id} data-testid={`badge-img-${f.id}`} src={`${API}/files/${f.storage_path}`} alt="Founding badge" className="aspect-square w-full rounded-lg border border-amber-500/20 object-cover" />
              ))}
            </div>
            {badges.length === 0 && (
              <div className="mt-5 flex h-32 items-center justify-center rounded-lg border border-dashed border-white/10">
                <Crown className="h-6 w-6 text-slate-700" />
              </div>
            )}
          </motion.div>
        </div>

        <GuardWork />
      </main>
    </div>
  );
}
